/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import { EntityType } from 'embercom/models/data/entity-types';
import {
  type RoleType,
  type MatchBehavior,
  type State,
  type Statistic,
} from 'embercom/models/data/matching-system/matching-constants';
import { type SenderType } from 'embercom/models/data/outbound/types';
import { type Status } from 'embercom/models/data/content-service/content-review-statuses';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import { type ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { setOwner } from '@ember/application';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { SEARCH_URL } from './constants';

type SearchPredicate = {
  attribute?: string;
  comparison?: string;
  value?: any;
  type: string;
};

export interface NormalizedContentServiceSearchResponse<TContentWrapper = ContentWrapper> {
  contentWrappers: Array<TContentWrapper>;
  lastPageHit: boolean;
  totalPages: number;
  totalCount: number;
  pageFrom: number;
  perPage: number;
  searchResponse?: ContentSearchResponse;
}

export interface ContentSearchParams {
  app_id: string;
  object_types: Array<EntityType>;
  title?: string;
  content_terms?: string;
  body_term?: string;
  url?: string;
  sender_type?: SenderType;
  sender_id?: string | number;
  sender_ids?: Array<string | number>;
  audience_types?: Array<RoleType>;
  states?: Array<State>;
  locales?: Array<string>;
  tag_ids?: Array<string | number>;
  tag_operator?: 'in' | 'nin' | 'IN' | 'NIN';
  match_behavior?: MatchBehavior;
  match_behaviors?: Array<MatchBehavior>;
  series_id?: string | number;
  hide_series?: boolean;
  any_tag_ids?: Array<string | number>;
  subscription_type_id?: number;
  per_page?: number;
  page_from?: number;
  sort_by?: string;
  sort_direction?: SortDirection;
  sort_by_content_type_first?: EntityType;
  statistics?: Array<Statistic>;
  wildcard_title_search?: boolean;
  additional_searchable_data?: Record<string, any>;
  predicates?: Array<SearchPredicate>;
  content_wrapper_types?: Array<ContentWrapperType>;
  content_review_statuses?: Array<Status>;
  folder_ids?: string[];
  folder_entity_type?: EntityType;
  last_updated_by_ids?: Array<string | number>;
  created_by_ids?: Array<string | number>;
  folder_filter_option?: FolderFilterOption;
  needs_review?: SyncIssuesFilterOptions;
  copilot_state?: number;
  chatbot_state?: number;
  add_highlight?: boolean;
  collection_ids?: Array<string | number>;
  linked_urls?: Array<string>;
  ai_content_segment_ids?: Array<string | number>;
  native_content?: boolean;
}

export type ContentServiceSearchAPIOptions = {
  persistToStore?: boolean;
};

export type StatusCounts = {
  total: number;
  failed_count: number;
  passed_count: number;
  excluded_count: number;
  status?: 'live' | 'syncing' | null;
};

export type FolderCounts = {
  status_counts: StatusCounts;
  chatbot_enabled: number;
  copilot_enabled: number;
  help_center_enabled: number;
};

export type ContentSearchResponse = {
  content_wrappers: Array<Record<string, any>>;
  page_from: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  last_page_hit: boolean;
  folder_counts?: Record<string, FolderCounts>;
};
export default class ContentServiceSearchAPI {
  persistToStore: boolean;

  constructor(owner: unknown, options: ContentServiceSearchAPIOptions = {}) {
    setOwner(this, owner);
    this.persistToStore = options.persistToStore ?? true;
  }

  @service declare notificationsService: any;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: any;

  async contentSearch<TContentWrapper = ContentWrapper>(
    searchParams: ContentSearchParams,
    _contentWrapperModel = 'content-service/content-wrapper',
  ): Promise<NormalizedContentServiceSearchResponse<TContentWrapper>> {
    let response = (await this.search(searchParams)) as ContentSearchResponse;

    return {
      contentWrappers: this.generateContentWrapperModels<TContentWrapper>(
        response.content_wrappers,
        _contentWrapperModel,
        response.folder_counts,
      ),
      pageFrom: response.page_from,
      perPage: response.per_page,
      totalCount: response.total_count,
      totalPages: response.total_pages,
      lastPageHit: response.last_page_hit,
      searchResponse: response,
    };
  }

  private generateContentWrapperModels<TContentWrapper>(
    objects: Array<any> = [],
    _contentWrapperModel: string,
    folderCounts?: Record<string, FolderCounts>,
  ): Array<TContentWrapper> {
    if (!this.persistToStore) {
      return [];
    }
    return objects.map((contentWrapper) => {
      // Inject folder counts if available
      if (folderCounts && contentWrapper.id && folderCounts[contentWrapper.content_wrapper_id]) {
        contentWrapper.folder_counts = folderCounts[contentWrapper.content_wrapper_id];
      }
      let hasImportRunRecords = contentWrapper.contents?.some(
        (content: any) => content.content_type === EntityType.ContentImportRunRecord,
      );

      if (hasImportRunRecords) {
        let normalizedObject = this.store.normalize(
          'content-service/content-import-run-record-wrapper',
          contentWrapper,
        );
        return this.store.push(normalizedObject) as TContentWrapper;
      }

      let normalizedObject = this.store.normalize(_contentWrapperModel, contentWrapper);
      return this.store.push(normalizedObject) as TContentWrapper;
    });
  }

  private search(params: ContentSearchParams) {
    return post(SEARCH_URL, params).catch(() => {
      this.notificationsService.notifyError(this.intl.t('content-service.search-error'));
      return {};
    });
  }
}

// Matches ContentService::Constants::KnowledgeHub::FolderFilterOptions in intercom
export enum FolderFilterOption {
  Any = 'any',
  None = 'none',
}

// Matches ContentService::Constants::KnowledgeHub::SyncIssuesFilterOptions in intercom
export enum SyncIssuesFilterOptions {
  HasSyncIssues = 'has_sync_issues',
  NoSyncIssues = 'no_sync_issues',
}
